import React, { useState, useEffect, useRef, useCallback } from "react";
import "./trophies.css";
import { FaTrophy, FaLock, FaTimes } from "react-icons/fa";

const STORAGE_KEY = "hb_trophies_v1";

// section === a real section[id] on the page → auto-unlocks when scrolled into view.
// section === null → unlocked by a custom action (first interaction, résumé, konami, platinum).
const TROPHIES = [
  { id: "start",      icon: "▶",  name: "Press Start",     desc: "You booted up the portfolio.",        rarity: "bronze",   section: null },
  { id: "about",      icon: "🧑", name: "Player Profile",  desc: "Scouted the About section.",          rarity: "bronze",   section: "about" },
  { id: "services",   icon: "🎛", name: "Loadout Check",   desc: "Reviewed what Harshit does.",         rarity: "bronze",   section: "services" },
  { id: "highlights", icon: "📊", name: "Stat Sheet",      desc: "Read the career highlights.",         rarity: "bronze",   section: "highlights" },
  { id: "experience", icon: "🗺", name: "Career Mode",     desc: "Explored the experience timeline.",   rarity: "silver",   section: "experience" },
  { id: "projects",   icon: "🎮", name: "Now Playing",     desc: "Browsed the shipped games.",          rarity: "silver",   section: "projects" },
  { id: "skills",     icon: "🌳", name: "Skill Tree",      desc: "Unlocked the full skill tree.",       rarity: "silver",   section: "skills" },
  { id: "resume",     icon: "📄", name: "Recruiter",       desc: "Opened the résumé. Respect.",         rarity: "gold",     section: null },
  { id: "contact",    icon: "🤝", name: "Co-op Invite",    desc: "Found the way to connect.",           rarity: "gold",     section: "contact" },
  { id: "platinum",   icon: "🏆", name: "Platinum — 100%", desc: "Explored the entire portfolio.",      rarity: "platinum", section: null },
  { id: "konami",     icon: "👾", name: "The Old Code",    desc: "↑ ↑ ↓ ↓ ← → ← → B A. You know it.",   rarity: "platinum", section: null, secret: true },
];

const VISIBLE = TROPHIES.filter((t) => !t.secret && t.id !== "platinum");
const byId = (id) => TROPHIES.find((t) => t.id === id);

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") || {};
  } catch {
    return {};
  }
};

const Trophies = () => {
  const [unlocked, setUnlocked] = useState(load);
  const [toasts, setToasts] = useState([]);
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const unlockedRef = useRef(unlocked);
  const btnRef = useRef(null);

  const unlock = useCallback((id) => {
    if (unlockedRef.current[id]) return;
    const trophy = byId(id);
    if (!trophy) return;

    const next = { ...unlockedRef.current, [id]: Date.now() };
    unlockedRef.current = next;
    setUnlocked(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* private mode */ }

    const key = `${id}_${Date.now()}`;
    setToasts((t) => [...t, { key, trophy }]);
    setPulse(true);
    setTimeout(() => setToasts((t) => t.filter((x) => x.key !== key)), 5200);
  }, []);

  // Auto-unlock each section trophy when it scrolls into the middle of the viewport.
  useEffect(() => {
    const sections = document.querySelectorAll("section[id]");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const t = TROPHIES.find((tr) => tr.section === e.target.id);
        if (t) unlock(t.id);
      }),
      { rootMargin: "-40% 0px -40% 0px" }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [unlock]);

  // "Press Start" on first real interaction.
  useEffect(() => {
    const fire = () => unlock("start");
    const opts = { once: true, passive: true };
    window.addEventListener("scroll", fire, opts);
    window.addEventListener("pointerdown", fire, opts);
    window.addEventListener("keydown", fire, opts);
    return () => {
      window.removeEventListener("scroll", fire);
      window.removeEventListener("pointerdown", fire);
      window.removeEventListener("keydown", fire);
    };
  }, [unlock]);

  // "Recruiter" — any click on a link to the résumé PDF.
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest && e.target.closest("a");
      if (a && a.href && a.href.toLowerCase().includes(".pdf")) unlock("resume");
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [unlock]);

  // Konami code — secret trophy.
  useEffect(() => {
    const seq = ["arrowup", "arrowup", "arrowdown", "arrowdown", "arrowleft", "arrowright", "arrowleft", "arrowright", "b", "a"];
    let pos = 0;
    const onKey = (e) => {
      const k = e.key.toLowerCase();
      pos = k === seq[pos] ? pos + 1 : k === seq[0] ? 1 : 0;
      if (pos === seq.length) { unlock("konami"); pos = 0; }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [unlock]);

  // Platinum when every visible trophy is earned.
  useEffect(() => {
    if (!unlocked.platinum && VISIBLE.every((t) => unlocked[t.id])) unlock("platinum");
  }, [unlocked, unlock]);

  // Close the cabinet with Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const reset = () => {
    unlockedRef.current = {};
    setUnlocked({});
    setToasts([]);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* private mode */ }
  };

  const count = VISIBLE.filter((t) => unlocked[t.id]).length;

  return (
    <>
      {/* Trophy pops (bottom-left) */}
      <div className="trophy-toasts" aria-live="polite" aria-atomic="false">
        {toasts.map(({ key, trophy }) => (
          <div key={key} className={`trophy-toast rarity-${trophy.rarity}`} role="status">
            <span className="trophy-toast__icon" aria-hidden="true">{trophy.icon}</span>
            <span className="trophy-toast__body">
              <span className="trophy-toast__label">Trophy Unlocked · {trophy.rarity}</span>
              <span className="trophy-toast__name">{trophy.name}</span>
            </span>
          </div>
        ))}
      </div>

      {/* HUD button (bottom-right) */}
      <button
        ref={btnRef}
        className={`trophy-hud${pulse ? " pulse" : ""}`}
        aria-haspopup="dialog"
        aria-label={`Open trophy cabinet — ${count} of ${VISIBLE.length} unlocked`}
        onClick={() => { setOpen(true); setPulse(false); }}
        onAnimationEnd={() => setPulse(false)}
      >
        <FaTrophy aria-hidden="true" />
        <span className="trophy-hud__count">{count}/{VISIBLE.length}</span>
      </button>

      {/* Trophy cabinet */}
      {open && (
        <div className="trophy-cabinet__backdrop" onClick={() => setOpen(false)}>
          <div
            className="trophy-cabinet"
            role="dialog"
            aria-modal="true"
            aria-label="Trophy Cabinet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="trophy-cabinet__head">
              <div>
                <h3>Trophy Cabinet</h3>
                <p className="trophy-cabinet__sub">{count} / {VISIBLE.length} explored{unlocked.platinum ? " · Platinum earned 🏆" : ""}</p>
              </div>
              <button className="trophy-cabinet__close" aria-label="Close trophy cabinet" onClick={() => setOpen(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="trophy-progress" aria-hidden="true">
              <span style={{ width: `${(count / VISIBLE.length) * 100}%` }} />
            </div>

            <ul className="trophy-list">
              {TROPHIES.map((t) => {
                const got = !!unlocked[t.id];
                const hidden = t.secret && !got;
                return (
                  <li key={t.id} className={`trophy-row rarity-${t.rarity}${got ? " got" : ""}`}>
                    <span className="trophy-row__icon" aria-hidden="true">
                      {got ? t.icon : <FaLock />}
                    </span>
                    <span className="trophy-row__body">
                      <span className="trophy-row__name">{hidden ? "Hidden Trophy" : t.name}</span>
                      <span className="trophy-row__desc">{hidden ? "Keep exploring to reveal this one." : t.desc}</span>
                    </span>
                    <span className="trophy-row__rarity">{t.rarity}</span>
                  </li>
                );
              })}
            </ul>

            <button className="trophy-reset" onClick={reset}>Reset progress</button>
          </div>
        </div>
      )}
    </>
  );
};

export default Trophies;
